import { useState, useEffect } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import type { LibraryDocument } from '@/lib/schemas'

interface EditDatesDialogProps {
  document: LibraryDocument
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Slice an ISO or 'YYYY-MM-DD' string down to the date portion an
 *  `<input type="date">` expects; empty for null/undefined. */
function toDateInput(value: string | null | undefined): string {
  if (!value) return ''
  return value.slice(0, 10)
}

export function EditDatesDialog({ document, open, onOpenChange }: EditDatesDialogProps) {
  const { updateDocumentDates } = useLibraryStore()

  const [effectiveDate, setEffectiveDate] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (open) {
      setEffectiveDate(toDateInput(document.effectiveDate))
      setExpiryDate(toDateInput(document.expiryDate))
      setIsSaving(false)
    }
  }, [open, document.effectiveDate, document.expiryDate])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Empty input clears the column (null); the PATCH dates branch accepts null.
      await updateDocumentDates(document.id, {
        effectiveDate: effectiveDate || null,
        expiryDate: expiryDate || null,
      })
      toast.success('Dates updated')
      onOpenChange(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update dates'
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[430px]">
        <DialogHeader>
          <DialogTitle>Edit dates</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{document.name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex gap-3">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="edit-effective-date">Effective date</Label>
              <Input
                id="edit-effective-date"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </div>
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="edit-expiry-date">Expiry date</Label>
              <Input
                id="edit-expiry-date"
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </div>
          </div>

          <p className="text-meta leading-relaxed">
            Expired documents are excluded from AI retrieval and won't be cited in drafts. Leave a
            field blank to clear it.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save dates
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
